// js/Component/ContactsReorderModal.jsx
//
// Drag-and-drop reorder modal shared by the legacy contacts list and the new
// address book. The caller supplies `items` (already mapped to
// { id, label, avatarSrc }) and an `onSave(orderedIds)` handler that persists
// the new order however it likes (typically by writing to a device setting).

import React, { useEffect, useMemo, useState } from "react";

function ContactsReorderModal({
    open,
    items = [],
    maxPerPage = 8,
    saving = false,
    onClose,
    onSave,
}) {
    const [orderList, setOrderList] = useState([]);
    const [dragIndex, setDragIndex] = useState(null);
    const [page, setPage] = useState(0);

    useEffect(() => {
        if (open) {
            setOrderList(items.map((i) => String(i.id)));
            setDragIndex(null);
            setPage(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const byId = useMemo(() => {
        const m = {};
        items.forEach((i) => {
            m[String(i.id)] = i;
        });
        return m;
    }, [items]);

    const handleDragStart = (index) => setDragIndex(index);
    const handleDragOver = (event) => event.preventDefault();
    const handleDrop = (index) => {
        if (dragIndex === null || dragIndex === index) return;
        setOrderList((prev) => {
            const updated = [...prev];
            const [moved] = updated.splice(dragIndex, 1);
            updated.splice(index, 0, moved);
            return updated;
        });
        setDragIndex(null);
    };

    const handleSaveClick = async () => {
        if (typeof onSave === "function") {
            await onSave(orderList);
        }
    };

    if (!open) return null;

    let rows = 2;
    let cols = 4;
    if (maxPerPage === 2) {
        rows = 1;
        cols = 2;
    } else if (maxPerPage === 3) {
        rows = 1;
        cols = 3;
    }
    const slotsPerPage = rows * cols;

    const totalPages = Math.max(1, Math.ceil(orderList.length / Math.max(slotsPerPage, 1)));
    const currentPage = Math.min(page, totalPages - 1);
    const pageIds = orderList.slice(
        currentPage * slotsPerPage,
        currentPage * slotsPerPage + slotsPerPage
    );

    const initialFor = (label) =>
        label && label.length > 0 ? label.charAt(0).toUpperCase() : "?";

    return (
        <div className="modal" role="dialog" style={{ display: "block" }}>
            <div className="modal-dialog" role="document" style={{ maxWidth: "920px" }}>
                <div className="modal-content" style={{ width: "900px", maxWidth: "90vw" }}>
                    <div className="modal-header">
                        <h1 className="modal-title">Order Contact list</h1>
                        <button
                            type="button"
                            className="close-button"
                            onClick={onClose}
                            disabled={saving}
                        >
                            &times;
                        </button>
                    </div>

                    <div className="modal-body">
                        <p style={{ fontSize: 13, color: "#555", marginBottom: 8 }}>
                            Drag on the left to change the order. The preview on the right
                            shows how contacts will appear on the device (left-to-right,
                            top-to-bottom).
                        </p>

                        <div style={{ display: "flex", gap: 24, alignItems: "flex-start", marginTop: 8 }}>
                            {/* Drag list */}
                            <div style={{ flex: "0 0 40%", minWidth: 260 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                                    Contacts order
                                </div>
                                <div
                                    style={{
                                        border: "1px solid #eee",
                                        borderRadius: 6,
                                        maxHeight: 360,
                                        overflowY: "auto",
                                    }}
                                >
                                    {orderList.length > 0 ? (
                                        orderList.map((id, index) => {
                                            const item = byId[id];
                                            const label = item?.label || String(id);
                                            const avatarSrc = item?.avatarSrc || null;
                                            return (
                                                <div
                                                    key={`${id}-${index}`}
                                                    draggable
                                                    onDragStart={() => handleDragStart(index)}
                                                    onDragOver={handleDragOver}
                                                    onDrop={() => handleDrop(index)}
                                                    style={{
                                                        padding: "8px 10px",
                                                        borderBottom:
                                                            index === orderList.length - 1
                                                                ? "none"
                                                                : "1px solid #eee",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 8,
                                                        cursor: "move",
                                                        backgroundColor:
                                                            dragIndex === index ? "#f5f5f5" : "#fff",
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            fontSize: 16,
                                                            color: "#999",
                                                            width: 12,
                                                            textAlign: "center",
                                                            userSelect: "none",
                                                        }}
                                                    >
                                                        ≡
                                                    </span>
                                                    <div
                                                        style={{
                                                            width: 32,
                                                            height: 32,
                                                            borderRadius: "50%",
                                                            overflow: "hidden",
                                                            backgroundColor: "#eee",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                        }}
                                                    >
                                                        {avatarSrc ? (
                                                            <img
                                                                src={avatarSrc}
                                                                alt={label}
                                                                style={{
                                                                    width: "100%",
                                                                    height: "100%",
                                                                    objectFit: "cover",
                                                                }}
                                                            />
                                                        ) : (
                                                            <span
                                                                style={{
                                                                    fontSize: 14,
                                                                    fontWeight: "bold",
                                                                    color: "#555",
                                                                }}
                                                            >
                                                                {initialFor(label)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div
                                                        style={{
                                                            flex: 1,
                                                            minWidth: 0,
                                                            display: "flex",
                                                            flexDirection: "column",
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                fontSize: 13,
                                                                fontWeight: 600,
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                            }}
                                                        >
                                                            {label}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div style={{ padding: 12, fontSize: 12, color: "#999" }}>
                                            No contacts configured yet.
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Preview */}
                            <div style={{ flex: 1 }}>
                                <div style={{ marginTop: 4 }}>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            marginBottom: 6,
                                        }}
                                    >
                                        <div style={{ fontSize: 13, color: "#555" }}>
                                            Preview{" "}
                                            <span style={{ color: "#999" }}>
                                                ({rows} rows × {cols} columns)
                                            </span>
                                        </div>
                                        {totalPages > 1 && (
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setPage((p) =>
                                                            Math.max(0, Math.min(p - 1, totalPages - 1))
                                                        )
                                                    }
                                                    disabled={currentPage === 0}
                                                    style={{
                                                        width: 28,
                                                        height: 28,
                                                        borderRadius: "50%",
                                                        border: "1px solid #ccc",
                                                        backgroundColor: "#fff",
                                                        cursor:
                                                            currentPage === 0 ? "not-allowed" : "pointer",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontSize: 16,
                                                        padding: 0,
                                                    }}
                                                >
                                                    ‹
                                                </button>
                                                <span style={{ fontSize: 12, color: "#666" }}>
                                                    Page {currentPage + 1} / {totalPages}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setPage((p) =>
                                                            Math.max(0, Math.min(p + 1, totalPages - 1))
                                                        )
                                                    }
                                                    disabled={currentPage === totalPages - 1}
                                                    style={{
                                                        width: 28,
                                                        height: 28,
                                                        borderRadius: "50%",
                                                        border: "1px solid #ccc",
                                                        backgroundColor: "#fff",
                                                        cursor:
                                                            currentPage === totalPages - 1
                                                                ? "not-allowed"
                                                                : "pointer",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontSize: 16,
                                                        padding: 0,
                                                    }}
                                                >
                                                    ›
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <div
                                        style={{
                                            backgroundColor: "#000",
                                            borderRadius: 8,
                                            padding: 12,
                                            width: "100%",
                                            minHeight: rows === 1 ? 140 : 190,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: "100%",
                                                display: "grid",
                                                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                                                gridTemplateRows: `repeat(${rows}, 1fr)`,
                                                gap: 8,
                                            }}
                                        >
                                            {Array.from({ length: slotsPerPage }).map((_, index) => {
                                                const id = pageIds[index];
                                                if (!id) {
                                                    return (
                                                        <div
                                                            key={`empty-${index}`}
                                                            style={{
                                                                backgroundColor: "#111",
                                                                borderRadius: 6,
                                                                border: "1px dashed #333",
                                                                minHeight: 70,
                                                            }}
                                                        />
                                                    );
                                                }
                                                const item = byId[id];
                                                const label = item?.label || String(id);
                                                const avatarSrc = item?.avatarSrc || null;
                                                return (
                                                    <div
                                                        key={`${id}-${index}`}
                                                        style={{
                                                            backgroundColor: "#111",
                                                            borderRadius: 6,
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            color: "#fff",
                                                            fontSize: 11,
                                                            padding: 6,
                                                            minHeight: 70,
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                width: 40,
                                                                height: 40,
                                                                borderRadius: 8,
                                                                backgroundColor: "#333",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                marginBottom: 4,
                                                                overflow: "hidden",
                                                            }}
                                                        >
                                                            {avatarSrc ? (
                                                                <img
                                                                    src={avatarSrc}
                                                                    alt={label}
                                                                    style={{
                                                                        width: "100%",
                                                                        height: "100%",
                                                                        objectFit: "cover",
                                                                    }}
                                                                />
                                                            ) : (
                                                                <span style={{ fontSize: 18, fontWeight: "bold" }}>
                                                                    {initialFor(label)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div
                                                            style={{
                                                                maxWidth: "100%",
                                                                textOverflow: "ellipsis",
                                                                overflow: "hidden",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            {label}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn cur-p btn-secondary"
                            onClick={onClose}
                            disabled={saving}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn cur-p btn-primary"
                            onClick={handleSaveClick}
                            disabled={saving}
                        >
                            {saving ? "Saving..." : "Save"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ContactsReorderModal;
